import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate, useParams } from 'react-router-dom';
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/ui/app-sidebar";
import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Toaster } from './components/ui/toaster';
import PrivateRoute from './wrappers/PrivateRoute';

// Import Pages
import HealthPage from './pages/HealthPage';
import CommunitiesPage from './pages/CommunityPages/CommunitiesPage';
import CommunityPage from './pages/CommunityPages/CommunityPage';
import CommunityEditPage from './pages/CommunityPages/CommunityEditPage';
import CommunityCreatePage from './pages/CommunityPages/CommunitiesCreatePage';
import CallPage from './pages/CallsPage';
import Login from './pages/Login/Login';
import EmailsPage from './pages/EmailsPage/EmailsPage';
import NotFoundPage from './pages/NotFoundPage/NotFoundPage';
import UsersPage from './pages/UsersPage/UsersPage';
import UsersCreatePage from './pages/UsersPage/UsersCreatePage';
import UserPage from './pages/UsersPage/UserPage';
import UnauthorizedPage from './pages/Unauthorized/Unauthorized';
import CompaniesPage from './pages/CompaniesPage/CompaniesPage';
import CompaniesCreate from './pages/CompaniesPage/CompaniesCreate';
import Signup from './pages/Signup/Signup';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import CompanyPage from './pages/CompaniesPage/CompanyPage';
import { useAuth } from '@clerk/clerk-react';
import Navbar from './components/NavBar/NavBar';
import CommunitySettingsPage from './pages/CommunityPages/Settings';
import AcceptInvite from './pages/AcceptInvite/AcceptInvite';
import AcceptInviteComplete from './pages/AcceptInvite/AcceptInviteComplete';
import SecretShopperPublic from './pages/SecretShopperPublic/SecretShopperPublic';
import axios from './lib/axios';
import { Skeleton } from './components/ui/skeleton';
import SecretShoppersPage from './pages/SecretShoppersPage';

const SecretShopperRedirect = () => {
    const { id } = useParams();
    return <Navigate to={`/public/secret-shoppers/${id}`} replace />;
};

const formatBreadcrumb = (segment) => {
    return segment
        .replace(/-/g, " ") // Replace dashes with spaces
        .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word
};

const MainLayout = ({ children }) => {
    const location = useLocation();
    const [communityName, setCommunityName] = useState("");
    const [loadingCommunity, setLoadingCommunity] = useState(false);
    const pathSegments = location.pathname
        .split("/")
        .filter((segment) => segment !== ""); // Remove empty segments

    useEffect(() => {
        const communityIndex = pathSegments.indexOf("communities");

        const getCommunityName = async (id) => {
            try {
                setLoadingCommunity(true);
                const { data } = await axios.get(`/communities/${id}`);
                setCommunityName(data?.community_name)
            } catch (error) {
                console.error(error);
            } finally {
                setLoadingCommunity(false);
            }
        }

        if (communityIndex === 1) {
            return;
        }

        if (pathSegments.length < communityIndex + 1) {
            return;
        }

        const idSegment = pathSegments[communityIndex + 1];

        if (!(/^\d+$/.test(idSegment))) {
            return;
        }

        getCommunityName(idSegment);
    }, [location.pathname]);

    return (
        <SidebarProvider>
            <div className="flex min-h-screen min-w-full px-8 pb-8">
                <AppSidebar />
                <SidebarInset className="flex flex-col flex-1">
                    <header className="w-full max-w-7xl mx-auto flex h-16 shrink-0 items-center">
                        <SidebarTrigger />
                        <Separator orientation="vertical" className="mr-2 h-4" />
                        <Breadcrumb>
                            <BreadcrumbList>
                                <BreadcrumbItem>
                                    <BreadcrumbLink href="/">
                                        Dashboard
                                    </BreadcrumbLink>
                                </BreadcrumbItem>
                                {pathSegments.map((segment, index) => {
                                    const path = `/${pathSegments.slice(0, index + 1).join("/")}`;
                                    const isLast = index === pathSegments.length - 1;
                                    const formattedSegment = formatBreadcrumb(segment);

                                    // Special case: Redirect "Communities" to "/"
                                    if (["communities", "emails", "calls"].includes(segment.toLowerCase())) {
                                        return null;
                                    }

                                    const isCommunityId =
                                        index > 0 && pathSegments[index - 1] === "communities" && /^\d+$/.test(segment);

                                    const displaySegment = isCommunityId
                                        ? loadingCommunity
                                            ? <Skeleton className="size-4 w-10 rounded-md" data-sidebar="menu-skeleton-icon" />
                                            : communityName || formattedSegment
                                        : formattedSegment;

                                    return (
                                        <React.Fragment key={path}>
                                            <BreadcrumbSeparator />
                                            <BreadcrumbItem>
                                                {isLast ? (
                                                    <BreadcrumbPage>{displaySegment}</BreadcrumbPage>
                                                ) : (
                                                    <BreadcrumbLink href={path} disabled={false}>
                                                        {displaySegment}
                                                    </BreadcrumbLink>
                                                )}
                                            </BreadcrumbItem>
                                        </React.Fragment>
                                    );
                                })}
                            </BreadcrumbList>
                        </Breadcrumb>
                    </header>
                    <div className='mt-8'>
                        {children}
                    </div>
                </SidebarInset>
            </div>
        </SidebarProvider>
    );
};

function Wrapper({ children }) {
    const { isSignedIn } = useAuth();

    if (isSignedIn) {
        return <MainLayout>{children}</MainLayout>
    }

    return (
        <div className="min-h-screen min-w-full flex flex-col">
            <Navbar />
            <div className="flex-1 flex justify-center"> {/* This div fills the remaining space */}
                {children}
            </div>
        </div>
    )
}

function App() {
    return (
        <Router>
            <Toaster />
            <Wrapper>
                <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/unauthorized" element={<UnauthorizedPage />} />
                    <Route path="/accept-invite" element={<AcceptInvite />} />
                    <Route path="/accept-invite-complete" element={<AcceptInviteComplete />} />
                    <Route path="public">
                        <Route path="secret-shopper/:id" element={<SecretShopperRedirect />} />
                        <Route path="secret-shoppers/:id" element={<SecretShopperPublic/>} />
                    </Route>

                    {/* Private Routes with Layout */}
                    <Route path="*" element={
                        <PrivateRoute>
                            <Routes>
                                <Route path="/login" element={<Login />} />
                                <Route path="/signup" element={<Signup />} />
                                <Route path="/forgot-password" element={<ForgotPassword />} />
                                <Route path="/unauthorized" element={<UnauthorizedPage />} />
                                <Route
                                    path="/"
                                    element={
                                        <PrivateRoute>
                                            <CommunitiesPage />
                                        </PrivateRoute>
                                    } />
                                <Route
                                    path="/health-page"
                                    element={
                                        <PrivateRoute adminOnly>
                                            <HealthPage />
                                        </PrivateRoute>
                                    } />
                                <Route
                                    path="/communities/:id"
                                    element={
                                        <PrivateRoute>
                                            <CommunityPage />
                                        </PrivateRoute>
                                    } />
                                <Route
                                    path="/communities/:id/edit"
                                    element={
                                        <PrivateRoute>
                                            <CommunityEditPage />
                                        </PrivateRoute>
                                    } />
                                <Route
                                    path="/communities/:id/settings"
                                    element={
                                        <PrivateRoute>
                                            <CommunitySettingsPage />
                                        </PrivateRoute>
                                    } />
                                <Route
                                    path="/communities/create"
                                    element={
                                        <PrivateRoute>
                                            <CommunityCreatePage />
                                        </PrivateRoute>
                                    } />
                                <Route
                                    path="/secret-shops"
                                    element={
                                        <PrivateRoute>
                                            <SecretShoppersPage />
                                        </PrivateRoute>
                                    } />
                                <Route
                                    path="/calls/:id"
                                    element={
                                        <PrivateRoute>
                                            <CallPage />
                                        </PrivateRoute>
                                    } />
                                <Route
                                    path="/emails/:id"
                                    element={
                                        <PrivateRoute>
                                            <EmailsPage />
                                        </PrivateRoute>
                                    } />
                                <Route
                                    path="/users"
                                    element={
                                        <PrivateRoute adminOnly>
                                            <UsersPage />
                                        </PrivateRoute>
                                    } />
                                <Route
                                    path="/users/create"
                                    element={
                                        <PrivateRoute adminOnly>
                                            <UsersCreatePage />
                                        </PrivateRoute>
                                    } />
                                <Route
                                    path="/users/:id"
                                    element={
                                        <PrivateRoute adminOnly>
                                            <UserPage />
                                        </PrivateRoute>
                                    } />
                                <Route
                                    path="/companies"
                                    element={
                                        <PrivateRoute adminOnly>
                                            <CompaniesPage />
                                        </PrivateRoute>
                                    } />
                                <Route
                                    path="/companies/:id"
                                    element={
                                        <PrivateRoute adminOnly>
                                            <CompanyPage />
                                        </PrivateRoute>
                                    } />
                                <Route
                                    path="/companies/create"
                                    element={
                                        <PrivateRoute adminOnly>
                                            <CompaniesCreate />
                                        </PrivateRoute>
                                    } />
                                <Route path="*" element={<NotFoundPage />} />
                            </Routes>
                        </PrivateRoute>
                    } />
                </Routes>
            </Wrapper>
        </Router>
    );
}

export default App;
